import { Grade, GradeClass } from '@mnspro-cloud/services';
import {
  ClassbookEntryResponse,
  CourseBasicResponse,
  Person,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { TFunction } from 'i18next';
import { orderBy } from 'lodash';
import { LessonCardClassbookEntry } from './Course/ClassbookEntry/ClassbookEntriesLessonCard';

export const UNKNOWN_GRADE_NAME = 'UNKNOWN_GRADE_NAME';
export const UNKNOWN_CLASS_NAME = 'UNKNOWN_CLASS_NAME';
export const UNKNOWN_COURSE_NAME = 'UNKNOWN_COURSE_NAME';

export const toLessonCardClassbookEntry = (
  entry: ClassbookEntryResponse,
): LessonCardClassbookEntry =>
  ({
    id: entry.id,
    author: {
      id: entry.createdBy.id,
      displayName: entry.createdBy.displayName,
      firstName: '',
      lastName: '',
      mailAddress: entry.createdBy.mailAddress,
    },
    comment: entry.comment ?? '',
    groupId: entry.groupId ?? '',
    definition: {
      id: entry.definition.id ?? '',
      name: entry.definition.shortName ?? '',
      categoryId: entry.definition.category?.id,
      categoryName: entry.definition.category?.shortName ?? '',
    },
    affectedUsers: entry.affectedUsers,
    occurred: entry.occured,
  } as LessonCardClassbookEntry);

export const replaceGradeNamePlaceHolders = (
  grades: Grade[],
  translate: (key: string, fallback: string) => string,
) => {
  const unknown = grades.find(g => g.name === UNKNOWN_GRADE_NAME);
  if (!unknown) {
    return grades;
  }

  return [
    ...grades.filter(g => g.id !== unknown.id),
    {
      ...unknown,
      name: replaceGradeNamePlaceholder(unknown.name, translate),
    },
  ];
};

export const replaceGradeNamePlaceholder = (
  name: string,
  translate: (key: string, fallback: string) => string,
) => {
  if (name === UNKNOWN_GRADE_NAME) {
    return translate(
      'settings:classbook:grades:unknown_grade',
      'Unbekannte Stufe',
    );
  }

  return name;
};

export const replaceClassNamePlaceholders = (
  classes: GradeClass[],
  translate: (key: string, fallback: string) => string,
) => {
  const unknown = classes.find(c => c.name === UNKNOWN_CLASS_NAME);
  if (!unknown) {
    return classes;
  }

  return [
    ...classes.filter(c => c.id !== unknown.id),
    {
      ...unknown,
      name: replaceClassNamePlaceholder(unknown.name, translate),
    },
  ];
};

export const replaceClassNamePlaceholder = (
  name: string,
  translate: (key: string, fallback: string) => string,
) => {
  if (name === UNKNOWN_CLASS_NAME) {
    return translate(
      'settings:classbook:grades:unknown_class',
      'Unbekannte Klasse',
    );
  }

  return name;
};
/// Combined all UnknownPlaceholder (Grade/Class/Course)
export const replaceUnknownPlaceHolder = (
  name: string,
  translate: (key: string, fallback: string) => string,
) => {
  if (name === UNKNOWN_CLASS_NAME) {
    return translate(
      'settings:classbook:grades:unknown_class',
      'Unbekannte Klasse',
    );
  }

  if (name === UNKNOWN_GRADE_NAME) {
    return translate(
      'settings:classbook:grades:unknown_grade',
      'Unbekannte Stufe',
    );
  }

  if (name === UNKNOWN_COURSE_NAME) {
    return translate(
      'settings:classbook:grades:unknown_course',
      'Unbekannter Kurs',
    );
  }

  return name;
};

/**
 * Builds the display name of a course: its name, followed by the classes it is assigned to
 * (e.g. "Mathematik (5a, 5b)").
 *
 * Class names are resolved (unknown-class placeholder), trimmed, sorted ascending and joined.
 * When no class name is left, the plain course name is returned: an empty "()" would read
 * like missing data (#11664).
 *
 * @param course Course whose display name should be built
 * @param translate Translation function used to resolve the unknown-class placeholder
 * @returns The course name, with the class names appended only when there is at least one
 */
export const getCourseNameWithClasses = (
  course: Pick<CourseBasicResponse, 'name' | 'classes'>,
  translate: TFunction,
): string => {
  const classNames = orderBy(
    (course.classes ?? [])
      .map((item) => replaceClassNamePlaceholder(item.name, translate).trim())
      .filter((name) => name !== ''),
  ).join(', ');

  return classNames === '' ? course.name : `${course.name} (${classNames})`;
};
